import React, { useRef, useState } from "react";
import CurrencyFormat from "react-currency-format";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

import PageLayout from "../../components/PageLayout";
import useIsMobile from "../../hooks/useIsMobile";
import ActionLoader from "../../components/ActionLoader";
import Toast from "../../components/Toast";

import { GET_LOAD_OUNCE_DASHBOARD_URL, getData } from "../../utils/api";

import InsightsHeaderIcon from "../../icons/newicons/insights.svg";

import "../Converters/converters.css";
import "./insights.css";
import { InsightsSubNav } from "./InsightsSubNav";

/** Local start of calendar day (00:00:00.000) from a Date (local). */
function startOfDayMs(date) {
  return new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    0,
    0,
    0,
    0
  ).getTime();
}

/** Local end of calendar day (23:59:59.999) from a Date (local). */
function endOfDayMs(date) {
  return new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    23,
    59,
    59,
    999
  ).getTime();
}

/** First day of the current month (local date; API uses start of that day). */
function getDefaultFromDate() {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), 1);
}

/** Today (local date; API uses end of that day). */
function getDefaultToDate() {
  return new Date();
}

function sameCalendarDay(a, b) {
  if (a == null || b == null) return false;
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

/** Optional numeric field from API; null if missing or NaN. */
function optionalOz(inner, key) {
  if (inner[key] == null) return null;
  const n = Number(inner[key]);
  return Number.isNaN(n) ? null : n;
}

const METAL_BY_TYPE_CONFIG = [
  {
    title: "Ceramic",
    ptKey: "ceramicpt",
    pdKey: "ceramicpd",
    rhKey: "ceramicrh",
  },
  {
    title: "Diesel",
    ptKey: "dieselpt",
    pdKey: "dieselpd",
    rhKey: "dieselrh",
  },
  {
    title: "After Market",
    ptKey: "aftermktpt",
    pdKey: "aftermktpd",
    rhKey: "aftermktrh",
  },
  {
    title: "Foil",
    ptKey: "foilpt",
    pdKey: "foilpd",
    rhKey: "foilrh",
  },
];

/** API: { data: { totalAmount (USD), pt, pd, rh, ceramic*, diesel*, … } } */
function parseOunceDashboard(res) {
  if (res == null || typeof res !== "object") return null;
  const inner = res.data != null && typeof res.data === "object" ? res.data : res;
  const totalAmount = inner.totalAmount;
  if (totalAmount == null || Number.isNaN(Number(totalAmount))) return null;
  return {
    totalAmount: Number(totalAmount),
    pt: optionalOz(inner, "pt"),
    pd: optionalOz(inner, "pd"),
    rh: optionalOz(inner, "rh"),
    byTypeMetalOz: METAL_BY_TYPE_CONFIG.map(({ title, ptKey, pdKey, rhKey }) => ({
      title,
      pt: optionalOz(inner, ptKey),
      pd: optionalOz(inner, pdKey),
      rh: optionalOz(inner, rhKey),
    })),
  };
}

function formatOzCell(value) {
  if (value == null || Number.isNaN(value)) {
    return "—";
  }
  return (
    <>
      {value.toLocaleString()}
      <span className="insights-table-oz c-red"> oz</span>
    </>
  );
}

/** Single table: overall + by converter type (compact). */
function InsightsMetalOzTable({ dashboardData }) {
  const typeRows = dashboardData?.byTypeMetalOz
    ? dashboardData.byTypeMetalOz.map((r) => ({
        key: r.title,
        label: r.title,
        pt: r.pt,
        pd: r.pd,
        rh: r.rh,
      }))
    : METAL_BY_TYPE_CONFIG.map((c) => ({
        key: c.title,
        label: c.title,
        pt: null,
        pd: null,
        rh: null,
      }));

  const rows = [
    {
      key: "all",
      label: "All",
      pt: dashboardData?.pt,
      pd: dashboardData?.pd,
      rh: dashboardData?.rh,
    },
    ...typeRows,
  ];

  return (
    <div className="insights-metal-table-wrap">
      <div className="c-red fw-bolder mb-10 fs-14">Metal (oz)</div>
      <table className="insights-metal-table">
        <thead>
          <tr>
            <th scope="col">Type</th>
            <th scope="col" className="t-right">
              Pt
            </th>
            <th scope="col" className="t-right">
              Pd
            </th>
            <th scope="col" className="t-right">
              Rh
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.key}>
              <th scope="row" className="insights-metal-table-type">
                {row.label}
              </th>
              <td className="t-right fw-bolder">{formatOzCell(row.pt)}</td>
              <td className="t-right fw-bolder">{formatOzCell(row.pd)}</td>
              <td className="t-right fw-bolder">{formatOzCell(row.rh)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const Insights = () => {
  const isMobile = useIsMobile();
  const [dateFrom, setDateFrom] = useState(() => getDefaultFromDate());
  const [dateTo, setDateTo] = useState(() => getDefaultToDate());
  const [lot, setLot] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [dashboardData, setDashboardData] = useState(null);
  const [toastParams, setToastParams] = useState({
    message: "",
    type: "",
    id: "",
  });
  const fetchTokenRef = useRef(0);

  const handleApplyFilters = async (e) => {
    e.preventDefault();
    const hasFrom = dateFrom != null;
    const hasTo = dateTo != null;
    if (hasFrom !== hasTo) {
      setToastParams({
        type: "ERROR",
        message: "Select both From and To dates, or leave both empty.",
        id: Date.now(),
      });
      return;
    }

    let params;
    if (hasFrom && hasTo) {
      if (startOfDayMs(dateFrom) > endOfDayMs(dateTo)) {
        setToastParams({
          type: "ERROR",
          message: "From date cannot be after To date.",
          id: Date.now(),
        });
        return;
      }
      params = {
        from: startOfDayMs(dateFrom),
        to: endOfDayMs(dateTo),
      };
    } else {
      params = { from: 0, to: 0 };
    }
    const lotTrim = (lot || "").trim();
    if (lotTrim) {
      params.lotFilter = lotTrim;
    }

    const token = ++fetchTokenRef.current;
    try {
      setIsLoading(true);
      const data = await getData(GET_LOAD_OUNCE_DASHBOARD_URL, params);
      if (token !== fetchTokenRef.current) return;
      const parsed = parseOunceDashboard(data);
      if (parsed == null) {
        setDashboardData(null);
        setToastParams({
          type: "ERROR",
          message: "Could not read dashboard data from the response.",
          id: Date.now(),
        });
      } else {
        setDashboardData(parsed);
      }
    } catch (err) {
      if (token !== fetchTokenRef.current) return;
      console.log("Insights dashboard error", err);
      setDashboardData(null);
      setToastParams({
        type: "ERROR",
        message: "Unable to load ounce insights. Please try again.",
        id: Date.now(),
      });
    } finally {
      if (token === fetchTokenRef.current) {
        setIsLoading(false);
      }
    }
  };

  const defaultFrom = getDefaultFromDate();
  const defaultTo = getDefaultToDate();
  const isDefaultDateRange =
    dateFrom != null &&
    dateTo != null &&
    sameCalendarDay(dateFrom, defaultFrom) &&
    sameCalendarDay(dateTo, defaultTo);

  const canClearFilters = Boolean(
    (lot || "").trim() ||
      dashboardData != null ||
      !isDefaultDateRange ||
      dateFrom == null ||
      dateTo == null
  );

  const handleClearFilters = () => {
    fetchTokenRef.current += 1;
    setIsLoading(false);
    setDateFrom(getDefaultFromDate());
    setDateTo(getDefaultToDate());
    setLot("");
    setDashboardData(null);
    setToastParams({ message: "", type: "", id: "" });
  };

  return (
    <PageLayout heading="Insights" hideSearchBar icon={InsightsHeaderIcon}>
      <InsightsSubNav />
      {isLoading ? <ActionLoader /> : null}
      <Toast
        type={toastParams.type}
        message={toastParams.message}
        id={toastParams.id}
      />
      <div
        style={{
          boxShadow: "0 5px 10px rgb(0, 0, 0, 0.3)",
          backgroundColor: "#fff",
          padding: isMobile ? "10px" : "20px",
          borderRadius: "20px",
          margin: "10px",
        }}
      >
        <form onSubmit={handleApplyFilters}>
          <div
            className={`flex ${isMobile ? "flex-column" : "flex-wrap"}`}
            style={{
              alignItems: isMobile ? "stretch" : "flex-end",
              gap: isMobile ? "16px" : "12px",
            }}
          >
            <div
              className="flex-1 insights-date-field"
              style={{ minWidth: isMobile ? "auto" : "160px" }}
            >
              <div className="c-red fw-bolder mb-12">From</div>
              <DatePicker
                selected={dateFrom}
                onChange={(d) => setDateFrom(d)}
                selectsStart
                startDate={dateFrom}
                endDate={dateTo}
                dateFormat="MM/dd/yyyy"
                placeholderText="mm/dd/yyyy"
                isClearable
                className="form-control rounded-pill font-primary fs-14 fRegular converter-formInput bg-blue wt-100p insights-date-input"
                wrapperClassName="insights-datepicker-wrap wt-100p"
              />
            </div>
            <div
              className="flex-1 insights-date-field"
              style={{ minWidth: isMobile ? "auto" : "160px" }}
            >
              <div className="c-red fw-bolder mb-12">To</div>
              <DatePicker
                selected={dateTo}
                onChange={(d) => setDateTo(d)}
                selectsEnd
                startDate={dateFrom}
                endDate={dateTo}
                minDate={dateFrom || undefined}
                dateFormat="MM/dd/yyyy"
                placeholderText="mm/dd/yyyy"
                isClearable
                className="form-control rounded-pill font-primary fs-14 fRegular converter-formInput bg-blue wt-100p insights-date-input"
                wrapperClassName="insights-datepicker-wrap wt-100p"
              />
            </div>
            <div
              className="flex-1"
              style={{ minWidth: isMobile ? "auto" : "200px" }}
            >
              <div className="c-red fw-bolder mb-12">Lot</div>
              <input
                type="text"
                value={lot}
                onChange={(e) => setLot(e.target.value)}
                placeholder="Lot number or name"
                className="form-control rounded-pill font-primary fs-14 fRegular mb-0 converter-formInput bg-blue"
                style={{
                  height: "45px",
                  paddingLeft: "16px",
                  backgroundColor: "#f1f1f1",
                  border: "1px solid #7b7b7b",
                }}
              />
            </div>
            <div
              className="flex"
              style={{
                marginTop: isMobile ? "4px" : "0",
                alignSelf: isMobile ? "stretch" : "flex-end",
                flexDirection: isMobile ? "column" : "row",
                gap: "8px",
                width: isMobile ? "100%" : "auto",
              }}
            >
              <button
                type="button"
                className="btn insights-btn-clear ht-40 flex flex-middle flex-center c-pointer wt-100p"
                style={{ minWidth: isMobile ? "100%" : "140px" }}
                disabled={!canClearFilters}
                onClick={handleClearFilters}
              >
                <span className="fw-bolder">Clear filters</span>
              </button>
              <button
                type="submit"
                className="btn ht-40 flex flex-middle flex-center c-pointer wt-100p"
                style={{ minWidth: isMobile ? "100%" : "160px" }}
              >
                <span className="fw-bolder">Apply filters</span>
              </button>
            </div>
          </div>
        </form>
      </div>

      <div
        style={{
          boxShadow: "0 5px 10px rgb(0, 0, 0, 0.3)",
          backgroundColor: "#fff",
          padding: isMobile ? "16px" : "28px",
          borderRadius: "20px",
          margin: "10px",
        }}
      >
        <div className="c-red fw-bolder mb-8 fs-14">Total amount</div>
        <div className="flex flex-middle flex-wrap mb-20">
          {dashboardData == null ? (
            <span className="insights-metric-value">—</span>
          ) : (
            <CurrencyFormat
              value={dashboardData.totalAmount}
              displayType="text"
              thousandSeparator
              prefix="$"
              renderText={(formatted) => (
                <span className="insights-metric-value">{formatted}</span>
              )}
            />
          )}
        </div>

        <InsightsMetalOzTable dashboardData={dashboardData} />

        <p className="c-gray fs-14 mt-16 mb-0" style={{ maxWidth: "520px" }}>
          Use a date range, a lot, or both. Leave From and To empty to filter
          without dates (for example lot-only).
        </p>
      </div>
    </PageLayout>
  );
};

export default Insights;
